/**
 * LOTO API
 * Freischaltpläne, Freischaltbereiche und Schnell-Rückmeldung
 */

import {defineStore} from "pinia";
import {ref} from "vue";
import {handleSupabaseError, supabase} from './client'
import {LotoArea, LotoAreaWithStats, LotoPlan} from "@/types/Loto";
import {Task} from "@/types/Order";
import {apiClient, t, useGlobalSetting} from "@/lib/utils";

// =====================================================
// PLÄNE
// =====================================================

export async function getLotoPlans(): Promise<LotoPlan[]> {
    const {data, error} = await supabase
        .from('loto_plans')
        .select('*')
        .order('group_name', {ascending: true})
        .order('sort_order', {ascending: true})
        .order('id', {ascending: true})

    if (error) handleSupabaseError(error)
    return data || []
}

export async function createLotoPlan(plan: Partial<LotoPlan>): Promise<LotoPlan> {
    const {data, error} = await supabase
        .from('loto_plans')
        .insert(plan)
        .select()
        .single()

    if (error) handleSupabaseError(error)
    return data
}

export async function updateLotoPlan(id: number, updates: Partial<LotoPlan>): Promise<LotoPlan> {
    const {data, error} = await supabase
        .from('loto_plans')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

    if (error) handleSupabaseError(error)
    return data
}

export async function deleteLotoPlan(plan: LotoPlan): Promise<void> {
    // Erst das Storage-Objekt entfernen, dann die Zeile (Cascade löscht Bereiche)
    if (plan.image_path) {
        await supabase.storage.from('loto-plans').remove([plan.image_path])
    }
    const {error} = await supabase
        .from('loto_plans')
        .delete()
        .eq('id', plan.id)

    if (error) handleSupabaseError(error)
}

// =====================================================
// PLAN-BILD (privater Bucket, Zugriff nur über Signed URL)
// =====================================================

export async function uploadPlanImage(planId: number, blob: Blob): Promise<string> {
    const path = `plan_${planId}_${Date.now()}.png`

    const {error: uploadError} = await supabase.storage
        .from('loto-plans')
        .upload(path, blob, {contentType: 'image/png', upsert: true})

    if (uploadError) throw uploadError

    // Altes Bild ersetzen + Pfad am Plan speichern
    const {data: plan} = await supabase.from('loto_plans').select('image_path').eq('id', planId).single()
    await updateLotoPlan(planId, {image_path: path})
    if (plan?.image_path && plan.image_path !== path) {
        await supabase.storage.from('loto-plans').remove([plan.image_path])
    }

    return path
}

export async function getPlanImageUrl(path: string): Promise<string | null> {
    const {data, error} = await supabase.storage
        .from('loto-plans')
        .createSignedUrl(path, 600)

    if (error) {
        console.error('Fehler beim Erstellen der Signed URL:', error)
        return null
    }
    return data?.signedUrl ?? null
}

// =====================================================
// BEREICHE
// =====================================================

export async function getLotoAreas(planId?: number): Promise<LotoArea[]> {
    let query = supabase
        .from('loto_areas')
        .select('*')
        .order('id', {ascending: true})

    if (planId != null) query = query.eq('plan_id', planId)

    const {data, error} = await query
    if (error) handleSupabaseError(error)
    return data || []
}

export async function getLotoAreasWithStats(planId: number): Promise<LotoAreaWithStats[]> {
    const {data, error} = await supabase
        .from('loto_areas_with_stats')
        .select('*')
        .eq('plan_id', planId)
        .order('id', {ascending: true})

    if (error) handleSupabaseError(error)
    return data || []
}

export async function createLotoArea(area: Partial<LotoArea>): Promise<LotoArea> {
    const {data, error} = await supabase
        .from('loto_areas')
        .insert(area)
        .select()
        .single()

    if (error) handleSupabaseError(error)
    return data
}

export async function updateLotoArea(id: number, updates: Partial<LotoArea>): Promise<LotoArea> {
    const {data, error} = await supabase
        .from('loto_areas')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

    if (error) handleSupabaseError(error)
    return data
}

export async function deleteLotoArea(id: number): Promise<void> {
    const {error} = await supabase
        .from('loto_areas')
        .delete()
        .eq('id', id)

    if (error) handleSupabaseError(error)
}

// =====================================================
// SCHNELL-RÜCKMELDUNG
// =====================================================

/**
 * Freischaltung mit einem Klick als "Vorläufig erledigt" melden.
 * Kein Body: der Server setzt state, quick_done_by und quick_done_at selbst.
 */
export const quickCompleteTask = async (uuid: string): Promise<Task | null> => {
    useGlobalSetting().loadingMessage = t("loading.quickComplete")
    useGlobalSetting().isLoading = true;
    try {
        const response = await apiClient.post(`/task/${uuid}/quickComplete`);
        useGlobalSetting().isLoading = false;
        return response.data?.data ?? null
    } catch (error) {
        useGlobalSetting().isLoading = false;
        console.error('Fehler bei der Schnell-Rückmeldung:', error)
        throw error
    }
}

// =====================================================
// BEREICHS-STORE (Client-Lookup für Listen: Name + Farbe)
// =====================================================

export const useLotoAreaStore = defineStore('lotoAreas', () => {
    const areas = ref<LotoArea[]>([])
    const plans = ref<LotoPlan[]>([])
    const loaded = ref(false)
    /** Zuletzt besuchte Freischaltliste – Ziel des Umschalters „Liste“ auf der Karte */
    const lastListPath = ref('/lockouts')

    async function load() {
        if (loaded.value) return
        await refresh()
    }

    async function refresh() {
        const [areaData, planData] = await Promise.all([getLotoAreas(), getLotoPlans()])
        areas.value = areaData
        plans.value = planData
        loaded.value = true
    }

    function areaById(id: number | null | undefined): LotoArea | undefined {
        if (id == null) return undefined
        return areas.value.find(a => a.id === id)
    }

    function planById(id: number | null | undefined): LotoPlan | undefined {
        if (id == null) return undefined
        return plans.value.find(p => p.id === id)
    }

    return {areas, plans, loaded, lastListPath, load, refresh, areaById, planById}
})
